/**
 * Book tab: the Compendium. Public, like the printed book; everyone has it
 * (v0.3: no passing). Patients on the player's list are pinned on top.
 */
import { useMemo, useState } from 'react';
import { COMPENDIUM, diag, RISK_IF_WRONG, type Diagnosis, type SeatView } from '@nightshift/engine/client';
import { TriageBadge } from '../ui/bits.tsx';

const TEST_NAME: Record<string, string> = {
  ASSESS: 'Bedside Assess',
  OPINION_PHYS: 'Physician opinion',
  OPINION_PSYCH: 'Psychiatry opinion',
  OPINION_SURG: 'Surgical opinion',
  SCAN: 'Scan',
};
const ROUTE_NAME: Record<string, string> = { S: 'Surgical', M: 'Medical', P: 'Psych', G: 'Standard', X: 'Exploratory' };
const OWNER: Record<string, string> = { EM: 'Em', SURG: 'Surg', PHYS: 'Phys', PSYCH: 'Psych', ICU: 'ICU', ANY: 'anyone' };

export function BookTab({ v }: { v: SeatView }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const mine = useMemo(() => {
    const names = new Set<string>();
    for (const b of v.beds) if (b.patient) names.add(b.patient.believed);
    for (const p of [...v.corridor, ...v.opened]) names.add(p.believed);
    // One entry per name: a trap card can share its true name with a real diagnosis
    // (e.g. "Very Hairy" is really Enthusiastic Beard), so prefer the entry whose front matches.
    const byName = new Map<string, Diagnosis>();
    for (const d of COMPENDIUM) {
      if (!names.has(d.name)) continue;
      const have = byName.get(d.name);
      if (!have || (have.frontName && have.frontName !== have.name && (!d.frontName || d.frontName === d.name))) byName.set(d.name, d);
    }
    return [...byName.values()];
  }, [v]);
  const entries = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const base = COMPENDIUM.filter((d) => d.special !== 'newborn' && (v.me.fiveSeat || !d.psychOnly));
    if (!needle) return base;
    return base.filter((d) => d.name.toLowerCase().includes(needle) || (d.frontName ?? '').toLowerCase().includes(needle) || d.differentials.some((k) => diag(k).name.toLowerCase().includes(needle)));
  }, [q, v.me.fiveSeat]);

  return (
    <div className="book">
      <label className="book-search">
        <span className="sr-only">Search the Compendium</span>
        <input className="input" type="search" placeholder="Search a diagnosis or a differential" value={q} onChange={(e) => setQ(e.target.value)} data-testid="book-search" />
        {q && (
          <button type="button" className="book-clear" onClick={() => setQ('')} aria-label="Clear the search" data-testid="book-clear">
            ×
          </button>
        )}
      </label>
      {q && mine.length > 0 && (
        <div className="book-mine" aria-label="On your list">
          {mine.map((d) => (
            <button
              key={d.key}
              type="button"
              className="chip"
              onClick={() => {
                setQ(d.name);
                setOpen(d.key);
              }}
              data-testid={`book-mine-${d.key}`}
            >
              {d.name}
            </button>
          ))}
        </div>
      )}
      {!q && mine.length > 0 && (
        <section aria-label="On your list">
          <h3 className="sec-title">On your list</h3>
          {mine.map((d) => (
            <Entry key={d.key} d={d} open={open === `m-${d.key}`} onToggle={() => setOpen(open === `m-${d.key}` ? null : `m-${d.key}`)} />
          ))}
        </section>
      )}
      <section aria-label="Compendium">
        <h3 className="sec-title">{q ? `${entries.length} match${entries.length === 1 ? '' : 'es'}` : 'The Compendium'}</h3>
        {entries.map((d) => (
          <Entry key={d.key} d={d} open={open === d.key} onToggle={() => setOpen(open === d.key ? null : d.key)} />
        ))}
        {entries.length === 0 && <p className="empty">Nothing by that name. Try part of the word.</p>}
      </section>
    </div>
  );
}

function Entry({ d, open, onToggle }: { d: Diagnosis; open: boolean; onToggle: () => void }) {
  return (
    <article className={`entry ${open ? 'open' : ''}`}>
      <button className="entry-head" onClick={onToggle} aria-expanded={open}>
        <TriageBadge colour={d.colour} size={24} />
        <span className="entry-name">{d.frontName && d.frontName !== d.name ? `${d.frontName} (${d.name})` : d.name}</span>
        <span className="entry-page">p.{d.page}</span>
      </button>
      {open && (
        <div className="entry-body">
          <dl className="entry-facts">
            <dt>Confirms it</dt>
            <dd>{d.definitive ? TEST_NAME[d.definitive] : 'Nothing confirms it; treat on judgement'}</dd>
            {d.differentials.length > 0 && (
              <>
                <dt>Could be</dt>
                <dd>
                  {d.differentials.map((k, i) => (
                    <span key={k}>
                      {i > 0 && '; '}
                      {diag(k).name}
                      {d.ruleOuts[k] ? `, ruled out by ${TEST_NAME[d.ruleOuts[k]!]}` : ''}
                    </span>
                  ))}
                </dd>
              </>
            )}
            {d.vent && (
              <>
                <dt>Ventilator</dt>
                <dd>Needs a ventilator in ICU (if ventilators are in play)</dd>
              </>
            )}
          </dl>
          {d.routes.map((r) => (
            <div key={r.code} className="entry-route">
              <p className="entry-route-head">
                <strong>{ROUTE_NAME[r.code]} route</strong>
                <span className="muted">
                  {' '}
                  {r.turns} turn{r.turns > 1 ? 's' : ''}; if wrong: <span className={`risk-${r.riskIfWrong}`}>{RISK_IF_WRONG[r.riskIfWrong]}</span>
                  {r.complRate ? `; complications ${r.complRate.toLowerCase()}` : ''}
                </span>
              </p>
              <ol className="rail compact">
                {r.steps.map((s, i) => (
                  <li key={i} className="todo">
                    <span>{s.label}</span>
                    <span className="rail-by">
                      {s.kind === 'THEATRE' ? 'Surg+ICU' : s.by.map((b) => OWNER[b] ?? b).join('/')}
                      {s.warn ? ', ⚠' : ''}
                      {s.blood ? `, ${s.blood} blood` : ''}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
          {d.notes && <p className="entry-notes">{d.notes}</p>}
        </div>
      )}
    </article>
  );
}
